import { CommonModule } from '@angular/common';
import { Component, input, OnChanges, OnDestroy, OnInit, Output, EventEmitter, SimpleChanges, computed, inject } from '@angular/core';
import { IWorkoutInfoResponse } from '@foxera/shared/interfaces/client.interface';
import { WorkoutInfoService } from '@foxera/feature/clients/services/workout-info.service';
import { CustomToastService } from '@foxera/shared/services/custom-toast.service';
import { WorkoutJobsStateService } from '@foxera/shared/services/workout-jobs-state.service';
import { ResponsiveService } from '@foxera/shared/services/responsive.service';
import { ButtonModule } from 'primeng/button';

interface WorkoutDataItem {
  icon: string;
  label: string;
  value: string;
  color: string;
}

type LoadingPhase = 1 | 2 | 3 | 4;

@Component({
  selector: 'app-live-generation-plan-loading',
  standalone: true,
  imports: [CommonModule, ButtonModule],
  templateUrl: './live-generation-plan-loading.component.html',
  styleUrls: ['./live-generation-plan-loading.component.css']
})
export class LiveGenerationPlanLoadingComponent implements OnInit, OnChanges, OnDestroy {
  workoutInfo = input<IWorkoutInfoResponse | null>(null);
  isLoading = input<boolean>(false);
  jobId = input<string | undefined>(undefined);
  @Output() stopGeneration = new EventEmitter<void>();
  @Output() hideInBackground = new EventEmitter<void>();
  
  private workoutInfoService = inject(WorkoutInfoService);
  private customToastService = inject(CustomToastService);
  private workoutJobsStateService = inject(WorkoutJobsStateService);
  public responsiveService = inject(ResponsiveService);
  private isCancelling: boolean = false;
  
  // Computed signal for formatted duration from service (mm:ss format)
  elapsedTimeFormatted = computed(() => {
    const jobId = this.jobId();
    return jobId ? this.workoutJobsStateService.getFullFormattedDuration(jobId) : '0:00';
  });
  
  // Computed signals for client names
  clientFullName = computed(() => {
    return this.workoutInfo()?.fullName || 'Client';
  });
  
  clientFirstName = computed(() => {
    const fullName = this.workoutInfo()?.fullName || '';
    return fullName.split(' ')[0] || 'Client';
  });
  
  clientLastName = computed(() => {
    const fullName = this.workoutInfo()?.fullName || '';
    const parts = fullName.split(' ');
    return parts.length > 1 ? parts.slice(1).join(' ') : '';
  });
  
  exercisesNames: string[] = [
    "Barbell Curl",
    "Incline Dumbbell Curl",
    "Hammer Curl",
    "Preacher Curl",
    "Concentration Curl",
    "Close-Grip Bench Press",
    "Skull Crushers",
    "Cable Triceps Pushdown",
    "Overhead Dumbbell Extension",
    "Weighted Dips",
    "EZ-Bar Curl",
    "Cable Curl (Straight Bar)",
    "Reverse Curl",
    "Rope Overhead Triceps Extension",
    "Single-Arm Cable Pushdown",
    "Spider Curl",
    "Bayesian Cable Curl",
    "Dumbbell Kickbacks",
    "Bench Dips",
    "Cross-Body Hammer Curl",
  
    "Standing Dumbbell Curl",
    "Alternating Dumbbell Curl",
    "Seated Dumbbell Curl",
    "Zottman Curl",
    "Drag Curl",
    "JM Press",
    "Floor Skull Crushers",
    "V-Bar Triceps Pushdown",
    "Single-Arm Overhead Extension",
    "Ring Dips",
    "Wide-Grip EZ Curl",
    "Cable Curl (EZ Bar)",
    "EZ-Bar Reverse Curl",
    "High Cable Triceps Extension",
    "Reverse-Grip Cable Pushdown",
    "Incline Spider Curl",
    "Low Cable Bayesian Curl",
    "Cable Triceps Kickback",
    "Parallel Bar Dips",
    "Neutral-Grip Hammer Curl",
  
    "Cheat Curl",
    "One-Arm Dumbbell Curl",
    "Preacher Machine Curl",
    "Cable Concentration Curl",
    "Resistance Band Curl",
    "Decline Close-Grip Bench Press",
    "EZ-Bar Skull Crushers",
    "Straight-Bar Pushdown",
    "Two-Hand Overhead Extension",
    "Assisted Dips",
    "Arm Blaster Curl",
    "Low Pulley Cable Curl",
    "Barbell Reverse Curl",
    "Cable Rope Extension",
    "Single-Arm Rope Pushdown",
    "Chest-Supported Spider Curl",
    "Single-Arm Bayesian Curl",
    "Incline Dumbbell Kickbacks",
    "Feet-Elevated Bench Dips",
    "Rope Hammer Curl",
  
    "Strict Barbell Curl",
    "Incline Cable Curl",
    "Seated Hammer Curl",
    "EZ-Bar Preacher Curl",
    "Dumbbell Concentration Curl",
    "Paused Close-Grip Bench Press",
    "Dumbbell Skull Crushers",
    "Cable Pushdown (Underhand)",
    "French Press",
    "Bodyweight Dips",
    "Wide-Grip Barbell Curl",
    "High Cable Curl",
    "Cable Reverse Curl",
    "Single-Arm Overhead Cable Extension",
    "Cross-Body Cable Pushdown",
    "EZ-Bar Spider Curl",
    "Cable Curl Behind Body",
    "Bent-Over Dumbbell Kickbacks",
    "Chair Dips",
    "Cable Cross-Body Hammer Curl"
  ];
  
  
  dataItemsCase1: WorkoutDataItem[] = [];
  dataItemsCase2: any[] = [];
  dataItemsCase3: string[] = [];
  dataItemsCase4: any[] = [];

  // Flow state (case1 -> case2 -> case3 -> case4 -> loop 3<->4)
  phase: LoadingPhase = 1;
  isCollapsing: boolean = false;

  // Case2 / Case4 message sequencing
  case2Index = 0;
  case4Index = 0;
  messageVisible = true;

  // ===== TIMING CONSTANTS (all durations in milliseconds) =====
  readonly pulseMs = 1300;                    // Pulse animation duration (cards & lines rhythm)
  readonly case1DurationMs = 5_000;          // Case 1: orbit phase duration before collapse
  readonly case2MessageDurationMs = 4_000;     // Case 2: how long each message stays visible
  readonly case2FadeDurationMs = 420;         // Case 2: fade out duration before switching messages
  readonly case3DurationMs = 10_000;            // Case 3: orbit phase duration before moving to case 4
  readonly case4MessageDurationMs = 4000;     // Case 4: how long each message stays visible
  readonly case4FadeDurationMs = 420;         // Case 4: fade out duration before switching messages
  readonly collapseCardDelayMs = 100;          // Delay between each card collapsing (sequential)
  readonly collapseAnimDurationMs = 800;      // Duration of the collapse-to-center animation
  readonly collapseExtraBufferMs = 200;       // Extra buffer time after last card collapses
  private timeouts: number[] = [];

  ngOnInit() {
    if (this.workoutInfo()) {
      this.initializeDataItems();
      if (this.isLoading()) this.startFlow();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    const loadingChanged = !!changes['isLoading'];
    const workoutInfoChanged = !!changes['workoutInfo'];

    if ((loadingChanged || workoutInfoChanged) && this.workoutInfo() && this.isLoading()) {
      this.initializeDataItems();
      this.startFlow();
    }

    if (changes['isLoading'] && !changes['isLoading'].currentValue) {
      this.stopFlow();
    }
  }

  ngOnDestroy() {
    this.stopFlow();
  }

  private initializeDataItems() {
    if (!this.workoutInfo()) return;
    
    const workoutInfo = this.workoutInfo()!;
    
    this.dataItemsCase1 = [
      {
        icon: 'pi pi-calendar',
        label: 'Weekly Commitment',
        value: `${workoutInfo.weeklyCommitment} training days per week`,
        color: 'purple'
      },
      {
        icon: 'pi pi-user',
        label: 'Body Metrics',
        value: `${workoutInfo.heightValue}cm • ${workoutInfo.weightValue}kg`,
        color: 'blue'
      },
      {
        icon: 'pi pi-chart-line',
        label: 'Fitness Level',
        value: `${workoutInfo.fitnessLevel}`,
        color: 'green'
      },
      {
        icon: 'pi pi-bullseye',
        label: 'Your Goal',
        value: workoutInfo.goal,
        color: 'orange'
      },
      {
        icon: 'pi pi-box',
        label: 'Boxing Training',
        value: workoutInfo.wantsBoxing ? 'Including boxing exercises' : 'Standard training focus',
        color: 'red'
      },
      {
        icon: 'pi pi-play-circle',
        label: 'Dominant Side',
        value: `${workoutInfo.dominantSide} dominant`,
        color: 'indigo'
      },
      {
        icon: 'pi pi-heart',
        label: 'Focus Muscles',
        value: this.formatFocusMuscles(),
        color: 'pink'
      },
      {
        icon: 'pi pi-exclamation-triangle',
        label: 'Health Considerations',
        value: this.formatHealthInfo(),
        color: 'amber'
      },
     
    ];

    this.dataItemsCase2 = [
      {
        label: `Thinking through the best plan for <span class="text-primary-600 font-semibold">${this.clientFullName()}</span>`,
      },
      {
        label: `Crafting a personalized strategy based on your focus: <span class="text-primary-600 font-semibold">${this.formatFocusMuscles()}</span>`,
      },
      {
        label: `I've found the best strategy for <span class="text-primary-600 font-semibold">${this.clientFirstName()}</span> — let's create it!`,
      },
      {
        label: `Now it's time for exercise selection!`,
      },
      {
        label: `Selecting challenging exercises suited for an <span class="text-primary-600 font-semibold">${workoutInfo.fitnessLevel}</span> level.`,
      },
      {
        label: this.getHealthConsiderationMessage(),
      },
      {
        label: `Perfect — let's choose the best exercises for <span class="text-primary-600 font-semibold">${this.clientFirstName()}</span>.`,
      },
    ];

    this.dataItemsCase3 = this.exercisesNames;

    this.dataItemsCase4 = [
      {
        label: `Selected the best and safest exercises for <span class="text-primary-600 font-semibold">${this.clientFirstName()}</span>'s use case.`,
      },
      {
        label: `Organizing exercises across training days using best practices to optimize <span class="text-primary-600 font-semibold">${this.clientFirstName()}</span>'s performance.`,
      },
      {
        label: 'Applying the final touches…',
      },
      {
        label: 'Validating everything…',
      },
      
      {
        label: `Some exercises don't fully align with <span class="text-primary-600 font-semibold">${this.clientFirstName()}</span>'s use case. Adjusting the selection…`,
      },
    ];
  }

  private formatFocusMuscles(): string {
    const focusMuscles = this.workoutInfo()?.focusMuscles;
    if (!focusMuscles || focusMuscles.length === 0) return 'Full body workout';

    if (focusMuscles.length <= 3) return focusMuscles.join(', ');
    return focusMuscles.slice(0, 3).join(', ') + '...';
  }

  private formatHealthInfo(): string {
    const pains = this.workoutInfo()?.pains || [];
    const posture = this.workoutInfo()?.postureProblems || [];
    const all = [...pains, ...posture];
    
    if (all.length === 0) return 'No health issues reported';
    if (all.length <= 2) return all.join(', ');
    return all.slice(0, 2).join(', ') + '...';
  }

  private getHealthConsiderationMessage(): string {
    const pains = this.workoutInfo()?.pains || [];
    const posture = this.workoutInfo()?.postureProblems || [];
    const hasPains = pains.length > 0;
    const hasPosture = posture.length > 0;

    // Case 1: No pains, no posture problems
    if (!hasPains && !hasPosture) {
      return 'No pain or posture problems detected. Proceeding with full exercise selection.';
    }

    // Case 2: Only pains
    if (hasPains && !hasPosture) {
      return `Detected <span class="text-primary-600 font-bold">${pains.join(', ')}</span>. Filtering exercises to ensure safety and avoid aggravating existing pain.`;
    }

    // Case 3: Only posture problems
    if (!hasPains && hasPosture) {
      return `Detected <span class="text-primary-600 font-bold">${posture.join(', ')}</span>. Selecting corrective exercises to address posture imbalances.`;
    }

    // Case 4: Both pains and posture problems
    return `Detected <span class="text-primary-600 font-bold">${pains.join(', ')}</span> and <span class="text-primary-600 font-semibold">${posture.join(', ')}</span>. Carefully filtering exercises to ensure safety while addressing posture issues.`;
  }

  private startFlow(): void {
    this.stopFlow();
    this.isCollapsing = false;
    this.case2Index = 0;
    this.case4Index = 0;
    this.messageVisible = true;

    // Mobile: Skip phase 1 and 3, only run phase 2 and 4
    if (this.responsiveService.isMobileView()) {
      this.phase = 2;
      this.startCase2();
      return;
    }

    // Desktop: Full flow (phase 1 -> 2 -> 3 -> 4)
    this.phase = 1;

    // Case 1: orbit -> collapse to center -> Case 2
    this.queueTimeout(() => {
      if (!this.isLoading()) return;
      this.isCollapsing = true;

      const collapseMs = this.getCollapseTotalMs(this.dataItemsCase1.length);
      this.queueTimeout(() => {
        if (!this.isLoading()) return;
        this.isCollapsing = false;
        this.startCase2();
      }, collapseMs);
    }, this.case1DurationMs);
  }

  private startCase2(): void {
    this.phase = 2;
    this.case2Index = 0;
    this.messageVisible = true;
    this.runMessageSequence(
      () => this.dataItemsCase2.map(x => x?.label).filter(Boolean) as string[],
      this.case2MessageDurationMs,
      this.case2FadeDurationMs,
      (idx) => (this.case2Index = idx),
      // Mobile: Skip phase 3, go directly to phase 4
      () => this.responsiveService.isMobileView() ? this.startCase4() : this.startCase3()
    );
  }

  private startCase3(): void {
    this.phase = 3;
    this.isCollapsing = false;

    // Case 3: orbit -> Case 4
    this.queueTimeout(() => {
      if (!this.isLoading()) return;
      this.startCase4();
    }, this.case3DurationMs);
  }

  private startCase4(): void {
    this.phase = 4;
    this.case4Index = 0;
    this.messageVisible = true;

    // Case 4: orbit design (like case3) but messages advance
    this.runMessageSequence(
      () => this.dataItemsCase4.map(x => x?.label).filter(Boolean) as string[],
      this.case4MessageDurationMs,
      this.case4FadeDurationMs,
      (idx) => (this.case4Index = idx),
      // Mobile: Loop back to phase 2, Desktop: Loop between case3 and case4
      () => this.responsiveService.isMobileView() ? this.startCase2() : this.startCase3()
    );
  }

  private runMessageSequence(
    getMessages: () => string[],
    perMessageMs: number,
    fadeMs: number,
    setIndex: (idx: number) => void,
    onDone: () => void
  ): void {
    const messages = getMessages();
    if (!messages.length) {
      onDone();
      return;
    }

    let idx = 0;
    setIndex(0);
    this.messageVisible = true;

    const tick = () => {
      if (!this.isLoading()) return;

      // fade out near the end of the 4s window
      this.queueTimeout(() => (this.messageVisible = false), Math.max(0, perMessageMs - fadeMs));

      // move to next
      this.queueTimeout(() => {
        if (!this.isLoading()) return;
        idx += 1;
        if (idx >= messages.length) {
          onDone();
          return;
        }
        setIndex(idx);
        this.messageVisible = true;
        tick();
      }, perMessageMs);
    };

    tick();
  }

  private stopFlow(): void {
    this.clearTimeouts();
    this.isCollapsing = false;
  }

  private queueTimeout(fn: () => void, ms: number): void {
    const id = window.setTimeout(fn, ms);
    this.timeouts.push(id);
  }

  private clearTimeouts(): void {
    for (const id of this.timeouts) window.clearTimeout(id);
    this.timeouts = [];
  }

  // Orbit helpers (cards + lines)
  orbitCardVars(index: number, total: number, radiusPx: number, scanDelayMs?: number): Record<string, string> {
    const angle = (360 / Math.max(total, 1)) * index;
    const angleNeg = -angle;
    return {
      '--angle': `${angle}deg`,
      '--angle-neg': `${angleNeg}deg`,
      '--radius': `${radiusPx}px`,
      '--pulse-ms': `${this.pulseMs}ms`,
      '--scan-delay': `${scanDelayMs ?? 0}ms`,
    };
  }

  getOrbitRadiusForCount(count: number): number {
    // Increased radius significantly to prevent card overlapping and ensure clear line visibility
    return 250;
  }

  getLineDelayMs(index: number, total: number): number {
    // Sequential line highlight that loops with the same rhythm as the pulse animation
    return (this.pulseMs * index) / Math.max(total, 1);
  }

  private getCollapseTotalMs(count: number): number {
    return (Math.max(count - 1, 0) * this.collapseCardDelayMs) + this.collapseAnimDurationMs + this.collapseExtraBufferMs;
  }

  /**
   * Handle stop generation button click - cancels the job via API then emits event to parent
   */
  onStopGeneration(): void {
    const currentJobId = this.jobId();
    if (this.isCancelling || !currentJobId) {
      return;
    }

    this.isCancelling = true;
    this.workoutInfoService.cancelJob(currentJobId).subscribe({
      next: () => {
        this.isCancelling = false;
        this.customToastService.showSuccess('Workout plan generation stopped');
        this.stopGeneration.emit();
      },
      error: () => {
        this.isCancelling = false;
        this.customToastService.showError('Failed to stop generation');
      }
    });
  }

  /**
   * Dismiss the loading screen without stopping the job –
   * the job keeps running and the user can track it from the navbar.
   */
  onHideInBackground(): void {
    this.hideInBackground.emit();
  }
}
